import React from 'react';
import styled, { css } from 'styled-components';
import {
  Box,
  FilterDropdown,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';
import { BrandProps, ColorProps } from '../../typings';
import CategorySwitcher from './CategorySwitcher';
import ShowAllFiltersButton from './ShowAllFiltersButton';
import SortFilter from './SortFilter';

const FiltersContainer = styled.div`
  ${({ theme: { media, spacing } }) => css`
    position: relative;
    width: 100%;
    height: 40px;
    margin-bottom: ${spacing.s2};

    ${media.up('slim')} {
      height: 48px;
    }
  `}
`;

const ScrollableContainer = styled.div`
  ${({ theme: { media } }) => css`
    position: absolute;
    display: flex;
    width: 100%;
    background: transparent;

    overflow-x: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    & .spark__menu {
      position: relative;
      max-width: 180px;

      ${media.up('slim')} {
        max-width: 260px;
      }
    }
  `}
`;

const DropdownContainer = styled(Box)`
  width: 148px;

  & .spark__menu {
    width: 196px;
  }

  & .spark__control {
    position: static;
    width: 148px;
  }
`;

interface BrandAndColorFilterProps extends BrandProps, ColorProps {}

export const BrandAndColorFilter = ({
  brandOptions,
  selectedBrandOptions,
  onBrandChange,
  colorOptions,
  selectedColorOptions,
  onColorChange,
}: BrandAndColorFilterProps) => {
  return (
    <>
      {brandOptions ? (
        <DropdownContainer data-testid="BrandFilters" ml="s2">
          <LinkRegion regionId="filtersTab.brand">
            <FilterDropdown
              selectedValues={selectedBrandOptions}
              onChange={onBrandChange}
              placeholder="Brand"
              options={brandOptions}
              isMulti
              isAlternate
            />
          </LinkRegion>
        </DropdownContainer>
      ) : null}

      {colorOptions ? (
        <DropdownContainer data-testid="ColorFilters" ml="s2">
          <LinkRegion regionId="filtersTab.color">
            <FilterDropdown
              selectedValues={selectedColorOptions}
              onChange={onColorChange}
              placeholder="Colour"
              options={colorOptions}
              isMulti
              isAlternate
              isMultiWithCircle
            />
          </LinkRegion>
        </DropdownContainer>
      ) : null}
    </>
  );
};

interface FilterProps extends BrandProps, ColorProps {
  subcategories: DeviceGalleryQuery['response']['deviceGallery']['subcategories'];
  selectedSubcategoryId: string;
  onSelectSubcategoryId: (subcategoryId: string) => void;
  selectedSubcategoryName: string;
  onShowAll: () => void;
}

export default function Filters({
  subcategories,
  selectedSubcategoryId,
  onSelectSubcategoryId,
  selectedSubcategoryName,
  brandOptions,
  selectedBrandOptions,
  onBrandChange,
  colorOptions,
  selectedColorOptions,
  onColorChange,
  onShowAll,
}: FilterProps) {
  const isBigView = useMediaQuery({ min: 'big' });

  return (
    <Box flexDirection="row" justifyContent="space-between">
      <FiltersContainer>
        <ScrollableContainer>
          <CategorySwitcher
            onSelectSubcategoryId={onSelectSubcategoryId}
            selectedSubcategoryId={selectedSubcategoryId}
            selectedSubcategoryName={selectedSubcategoryName}
            subcategories={subcategories}
          />
          <BrandAndColorFilter
            brandOptions={brandOptions}
            selectedBrandOptions={selectedBrandOptions}
            onBrandChange={onBrandChange}
            colorOptions={colorOptions}
            selectedColorOptions={selectedColorOptions}
            onColorChange={onColorChange}
          />
          <LinkRegion regionId="filter">
            <ShowAllFiltersButton onShowAll={onShowAll} />
          </LinkRegion>
        </ScrollableContainer>
      </FiltersContainer>
      {isBigView ? <SortFilter /> : null}
    </Box>
  );
}
